import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import {
  ITableSortParams,
  TableComponent,
  TableSortOrder,
  toSortOrder,
} from '../../../components/data-display/table/table.component';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { TableHeaderComponent } from '../../../components/data-display/table/table-header/table-header.component';
import { Subscription } from 'rxjs';
import { TextInputComponent } from '../../../components/data-input/form-inputs/text-input/text-input.component';
import {
  DATE_FORMAT_LONG,
  DEFAULT_LANGUAGES,
  ETextType,
  getTemplateCode,
  ILanguage,
  IMessageText,
  isTemplateCode,
  ITextLanguageChangedValue,
  ITextLanguageValue,
} from '@web/dtos';
import { LanguageStore } from '../../../services/api/language/language-store.service';
import { CorporateDesignStore } from '../../../services/api/corporate-design/corporate-design-store.service';
import { MessageStore } from '../../../services/api/message/message-store.service';
import { IDialogData } from '../../../components/dialogs/dialog';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { TableCellComponent } from '../../../components/data-display/table/table-cell/table-cell.component';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { ImportExportService } from '../../../services/import-export/import-export.service';
import { ImportExportGateway } from '../../../services/api/gateways';
import { MessageGateway } from '../../../services/api/message/message.gateway';

interface ISearchForm {
  search: FormControl<string | null>;
}

@Component({
  selector: 'aw-user-feedback-page',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    SectionHeaderActionsComponent,
    ButtonComponent,
    LoadingAnimationComponent,
    SectionComponent,
    TableComponent,
    AppendTestIdPipe,
    TableHeaderComponent,
    TextInputComponent,
    TableCellComponent,
  ],
  templateUrl: './user-feedback-page.component.html',
  styles: ``,
  providers: [
    ImportExportService,
    {
      provide: ImportExportGateway<IMessageText[], FormData>,
      useClass: MessageGateway,
    },
  ],
})
export class UserFeedbackPageComponent implements OnDestroy {
  private readonly dialogService: DialogService = inject(DialogService);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  readonly messageStore: MessageStore = inject(MessageStore);
  readonly languageStore: LanguageStore = inject(LanguageStore);
  readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly importExportService: ImportExportService<
    IMessageText[],
    ETextType
  > = inject(ImportExportService<IMessageText[], ETextType>);

  editingData: any | null = null;
  editingLangPrefix: string | null = null;

  protected readonly DATE_FORMAT_LONG: string = DATE_FORMAT_LONG;

  public readonly filterForm: FormGroup<ISearchForm> = new FormGroup({
    search: new FormControl<string | null>(null),
  });
  filename: FormControl = new FormControl();
  public readonly componentSubscriptions: Subscription = new Subscription();
  protected activeLanguages: ILanguage[] = DEFAULT_LANGUAGES;
  protected readonly isTemplateCode: typeof isTemplateCode = isTemplateCode;
  protected readonly getTemplateCode: typeof getTemplateCode = getTemplateCode;

  public allMessages: IMessageText[] = this.messageStore.messages();
  public filteredMessages: IMessageText[] = this.allMessages;

  constructor() {
    effect((): void => {
      this.allMessages = this.messageStore.messages();
      this.filteredMessages = this.allMessages;
    });

    this.componentSubscriptions.add(
      this.filterForm.valueChanges.subscribe(value => {
        this.filteredMessages = this.filterMessages(
          this.allMessages,
          this.toMessageFilterValue(value as any)
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.componentSubscriptions.unsubscribe();
  }

  save(): void {
    const changedMessages: IMessageText[] = [];
    this.allMessages.forEach((messageText: IMessageText) => {
      let hasChangedMessages = false;
      messageText.text.values.forEach((value: ITextLanguageChangedValue) => {
        if (value.isChanged) {
          hasChangedMessages = true;
        }
      });
      if (hasChangedMessages) {
        changedMessages.push(messageText);
      }
    });
    this.messageStore.save(changedMessages);
  }

  get hasChanges(): boolean {
    return !!this.allMessages.find(message =>
      message.text.values.find(
        (textValueForLanguage: ITextLanguageChangedValue) =>
          textValueForLanguage.isChanged
      )
    );
  }

  startEdit(data: any, language: ILanguage): void {
    this.editingData = data;
    this.editingLangPrefix = language.prefix;
  }

  stopEdit(): void {
    this.editingData = null;
    this.editingLangPrefix = null;
    this.cdRef.detectChanges();
  }

  isEditing(data: any | null, language: ILanguage): boolean {
    return (
      this.editingData?.entityType === data?.entityType &&
      this.editingData?.relationType === data?.relationType &&
      this.editingData?.id === data?.id &&
      this.editingLangPrefix === language.prefix
    );
  }

  exportMessages(): void {
    this.importExportService.downloadFile(ETextType.Web, 'shop-messages.csv');
  }

  onValueChange(newValue: string, data: any, language: ILanguage): void {
    const languageMessage: IMessageText = data as IMessageText;
    this.allMessages = this.allMessages.map(
      (message: IMessageText): IMessageText => {
        return this.changeValueInMessages(
          message,
          languageMessage,
          language,
          newValue
        );
      }
    );
    this.filteredMessages = this.filteredMessages.map(
      (message: IMessageText): IMessageText => {
        return this.changeValueInMessages(
          message,
          languageMessage,
          language,
          newValue
        );
      }
    );
  }

  private changeValueInMessages(
    messageText: IMessageText,
    languageMessage: IMessageText,
    language: ILanguage,
    newValue: string
  ): IMessageText {
    if (messageText.id === languageMessage.id) {
      let hasChangedMessages = false;
      const newValues: (ITextLanguageValue | ITextLanguageChangedValue)[] =
        messageText.text.values.map(
          (
            val: ITextLanguageValue | ITextLanguageChangedValue
          ): ITextLanguageValue | ITextLanguageChangedValue => {
            if (language.prefix === val.languagePrefix) {
              hasChangedMessages = true;
              return { ...val, value: newValue, isChanged: true };
            }
            return val;
          }
        );
      if (!hasChangedMessages) {
        return {
          ...messageText,
          text: {
            ...messageText.text,
            values: [
              ...newValues,
              {
                languagePrefix: language.prefix,
                value: newValue,
                isChanged: true,
              },
            ],
          },
        };
      }
      return { ...messageText, text: { values: newValues } };
    } else {
      return messageText;
    }
  }

  reload(): void {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe((result: any) => {
      if (result != undefined && result.submit) {
        this.messageStore.reload();
      }
    });
  }

  public filterMessages(
    allSubscriptions: IMessageText[],
    filter: any
  ): IMessageText[] {
    return allSubscriptions.filter((s: IMessageText): boolean =>
      this.searchIncludesSubstring(s, filter.search)
    );
  }

  private searchIncludesSubstring(
    subscription: IMessageText,
    subStringFilter: string | null | undefined
  ): boolean {
    if (!subStringFilter) {
      return true;
    }

    const filter: string = subStringFilter.toLowerCase();

    return (
      subscription.code?.toLowerCase().includes(filter) ||
      subscription.text.values.some((field): boolean =>
        field?.value.toLowerCase().includes(filter)
      )
    );
  }

  private toMessageFilterValue(value: any): any {
    const compensateNull = <T>(value: T) => (value === 'null' ? null : value);

    return {
      search: compensateNull(value.search),
    };
  }

  public messageCodeFilter: ITableSortParams<IMessageText>['compare'] = (
    a: IMessageText,
    b: IMessageText,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    return a.code.toLowerCase() > b.code.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public messageValueFilter: ITableSortParams<IMessageText>['compare'] = (
    a: IMessageText,
    b: IMessageText,
    order: TableSortOrder,
    lang?: string
  ): number => {
    const sortDirection = toSortOrder(order);
    const aValue = a.text.values.filter(val => val.languagePrefix === lang)[0];
    const bValue = b.text.values.filter(val => val.languagePrefix === lang)[0];

    if (aValue === undefined) {
      return 1 * sortDirection;
    }
    if (bValue === undefined) {
      return -1 * sortDirection;
    }
    return aValue.value.toLowerCase() > bValue.value.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public dateLastEditedAtFilter: ITableSortParams<IMessageText>['compare'] = (
    a: IMessageText,
    b: IMessageText,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    const dateA = new Date(a.text.lastEditedAt ?? new Date());
    const dateB = new Date(b.text.lastEditedAt ?? new Date());
    return (dateA.getTime() - dateB.getTime()) * sortDirection;
  };

  isActiveLanguages(language: ILanguage): boolean {
    return this.activeLanguages.some(l => l.prefix === language.prefix);
  }
  toggleLanguage(language: ILanguage) {
    if (this.activeLanguages.some(l => l.prefix === language.prefix)) {
      this.activeLanguages = this.activeLanguages.filter(l => l !== language);
    } else {
      this.activeLanguages.push(language);
    }
  }

  getLanguageValue(data: any, language: ILanguage): string {
    const messageText: IMessageText = data as IMessageText;
    return (
      messageText.text.values.filter(
        (l: ITextLanguageChangedValue) => l.languagePrefix === language.prefix
      )[0]?.value ?? ''
    );
  }
  getLanguageIsChanged(data: any, language: ILanguage): boolean {
    const messageText: IMessageText = data as IMessageText;
    return (
      (messageText.text.values as ITextLanguageChangedValue[]).filter(
        (textValueForLanguage: ITextLanguageChangedValue) =>
          textValueForLanguage.languagePrefix === language.prefix
      )[0]?.isChanged ?? false
    );
  }

  getChangedCountForLang(language: ILanguage): number {
    let count = 0;
    this.allMessages.forEach((normalText: IMessageText) => {
      if (
        normalText.text.values.filter(
          (val: ITextLanguageChangedValue) =>
            val.languagePrefix === language.prefix && val.isChanged
        ).length > 0
      ) {
        count++;
      }
    });
    return count;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    this.uploadFile(input.files[0]);
    input.value = '';
  }

  uploadFile(file: File): void {
    const formData = new FormData();
    formData.append('file', file);

    this.importExportService.uploadFile(formData).subscribe({
      next: res => {
        if (res?.message?.type === 'success') {
          const changedMessages: IMessageText[] = res.data!.map(message => {
            message.text.values.forEach(
              (textValue: ITextLanguageChangedValue) => {
                textValue.isChanged = true;
              }
            );
            return message;
          });
          this.messageStore.updateMessageText(changedMessages);
        }
      },
      error: err => console.error('Fehler beim Upload', err),
    });
  }
}
